// Theme configuration for Alt-X Frontend
// Centralized design tokens for colors, borders, and other styling properties

export interface Theme {
  colors: {
    // Background colors
    background: {
      primary: string;
      secondary: string;
      tertiary: string;
      card: string;
      overlay: string;
      loading: string;
      error: string;
      errorLight: string;
      warning: string;
      warningLight: string;
      white: string;
      gray50: string;
      gray100: string;
      gray200: string;
      gray400: string;
    };
    
    // Text colors
    text: {
      primary: string;
      secondary: string;
      tertiary: string;
      muted: string;
      placeholder: string;
      inverse: string;
      loading: string;
      info: string;
      warning: string;
      warningLight: string;
      warningText: string;
      error: string;
      errorLight: string;
      errorHover: string;
      gray400: string;
      gray500: string;
      gray600: string;
      gray700: string;
      gray800: string;
      gray900: string;
    };
    
    // Brand colors
    brand: {
      primary: string;
      primaryHover: string;
      primaryLight: string;
      accent: string;
    };
    
    // Status colors
    status: {
      success: string;
      successHover: string;
      successLight: string;
      successText: string;
      warning: string;
      warningHover: string;
      warningLight: string;
      warningText: string;
      warningBackground: string;
      error: string;
      errorHover: string;
      errorLight: string;
      errorText: string;
      errorBackground: string;
      info: string;
      infoHover: string;
      infoLight: string;
      infoText: string;
    };
    
    // Border colors
    border: {
      primary: string;
      secondary: string;
      tertiary: string;
      dashed: string;
      dashedHover: string;
      divider: string;
      error: string;
      warning: string;
      loading: string;
    };
    
    // Interactive states
    interactive: {
      hover: string;
      active: string;
      disabled: string;
      disabledText: string;
      focus: string;
    };
    
    // Chart colors
    chart: {
      primary: string;
      secondary: string;
      accent: string;
      danger: string;
      purple: string;
      pink: string;
      teal: string;
      orange: string;
      grid: string;
      axis: string;
      dotStroke: string;
      tooltip: {
        background: string;
        border: string;
        text: string;
        textSecondary: string;
      };
    };
    
    // UI colors for badges, buttons, info boxes
    ui: {
      blue: {
        background: string;
        backgroundLight: string;
        text: string;
        textDark: string;
        border: string;
        borderLight: string;
      };
      green: {
        background: string;
        backgroundLight: string;
        text: string;
        textDark: string;
      };
      red: {
        background: string;
        backgroundLight: string;
        text: string;
        textDark: string;
        border: string;
      };
      yellow: {
        background: string;
        backgroundLight: string;
        text: string;
        textDark: string;
        border: string;
      };
      orange: {
        background: string;
        backgroundLight: string;
        text: string;
        textDark: string;
      };
      purple: {
        background: string;
        backgroundLight: string;
        text: string;
        border: string;
        borderLight: string;
      };
      indigo: {
        background: string;
        backgroundLight: string;
      };
    };
    
    // PropertyAnalysis-specific colors
    propertyAnalysis: {
      pageBackground: string;
      headerBackground: string;
      cardBackground: string;
      cardBackgroundNested: string;
      animatedEffect1: string;
      animatedEffect2: string;
      animatedEffect3: string;
      shadow: string;
    };
    
    // Recommendation badge colors (BUY/HOLD/AVOID/NEGOTIATE)
    recommendation: {
      buy: {
        background: string;
        text: string;
        border: string;
        textHeading: string;
        textBullet: string;
      };
      hold: {
        background: string;
        text: string;
        border: string;
        textHeading: string;
        textBullet: string;
      };
      avoid: {
        background: string;
        text: string;
        border: string;
        textHeading: string;
        textBullet: string;
      };
      negotiate: {
        background: string;
        text: string;
        border: string;
        textHeading: string;
        textBullet: string;
      };
      neutral: {
        background: string;
        text: string;
        border: string;
        textHeading: string;
        textBullet: string;
      };
    };
    
    // Action item colors (immediate/short-term/long-term)
    actionItems: {
      immediate: {
        background: string;
        border: string;
        textHeading: string;
        textBullet: string;
      };
      shortTerm: {
        background: string;
        border: string;
        textHeading: string;
        textBullet: string;
      };
      longTerm: {
        background: string;
        border: string;
        textHeading: string;
        textBullet: string;
      };
    };
    
    // Tab colors
    tabs: {
      active: {
        text: string;
        border: string;
      };
      inactive: {
        text: string;
        hover: string;
      };
    };
    
    // Validation/error colors
    validation: {
      errorText: string;
      errorIndicator: string;
      errorTitle: string;
    };
    
    // Value display colors
    values: {
      fairMarketValue: string;
      recommendedOffer: string;
      positive: string;
      negative: string;
    };
    
    // Live data badge
    liveData: {
      background: string;
      text: string;
      border: string;
    };
  };
  
  // Border thicknesses
  borders: {
    thin: string;
    medium: string;
    thick: string;
    dashed: string;
    bubble: string;
  };
  
  // Spacing scale
  spacing: {
    xs: string;
    sm: string;
    md: string;
    lg: string;
    xl: string;
    xxl: string;
  };
  
  // Border radius
  radius: {
    sm: string;
    md: string;
    lg: string;
    full: string;
  };
  
  // Opacity values
  opacity: {
    light: string;
    medium: string;
    heavy: string;
  };
}

// Default dark theme - Black and White
export const darkTheme: Theme = {
  colors: {
    background: {
      primary: 'bg-black',
      secondary: 'bg-gray-900',
      tertiary: 'bg-gray-800',
      card: 'bg-gray-900',
      overlay: 'bg-black/50',
      loading: 'bg-gray-800/30',
      error: 'bg-gray-900/20',
      errorLight: 'bg-gray-800/30',
      warning: 'bg-gray-900/20',
      warningLight: 'bg-gray-600',
      white: 'bg-white',
      gray50: 'bg-gray-50',
      gray100: 'bg-gray-100',
      gray200: 'bg-gray-200',
      gray400: 'bg-gray-400',
    },
    
    text: {
      primary: 'text-white',
      secondary: 'text-gray-300',
      tertiary: 'text-gray-400',
      muted: 'text-gray-500',
      placeholder: 'placeholder-gray-500',
      inverse: 'text-black',
      loading: 'text-gray-600',
      info: 'text-gray-300',
      warning: 'text-gray-300',
      warningLight: 'text-gray-200',
      warningText: 'text-gray-100',
      error: 'text-gray-300',
      errorLight: 'text-gray-200',
      errorHover: 'text-gray-300',
      gray400: 'text-gray-400',
      gray500: 'text-gray-500',
      gray600: 'text-gray-600',
      gray700: 'text-gray-700',
      gray800: 'text-gray-800',
      gray900: 'text-gray-900',
    },
    
    brand: {
      primary: 'bg-white',
      primaryHover: 'hover:bg-gray-100',
      primaryLight: 'text-gray-300',
      accent: 'text-white',
    },
    
    status: {
      success: 'bg-white',
      successHover: 'hover:bg-gray-100',
      successLight: 'text-gray-300',
      successText: 'text-black',
      warning: 'bg-gray-600',
      warningHover: 'hover:bg-gray-700',
      warningLight: 'text-gray-300',
      warningText: 'text-gray-200',
      warningBackground: 'bg-gray-900/20',
      error: 'bg-gray-600',
      errorHover: 'hover:bg-gray-700',
      errorLight: 'text-gray-300',
      errorText: 'text-gray-200',
      errorBackground: 'bg-gray-900/20',
      info: 'bg-gray-600',
      infoHover: 'hover:bg-gray-700',
      infoLight: 'text-gray-300',
      infoText: 'text-gray-200',
    },
    
    border: {
      primary: 'border-gray-800',
      secondary: 'border-gray-700',
      tertiary: 'border-gray-600',
      dashed: 'border-gray-700',
      dashedHover: 'hover:border-gray-600',
      divider: 'border-gray-900',
      error: 'border-red-600/30',
      warning: 'border-yellow-600/30',
      loading: 'border-gray-700/50',
    },
    
    interactive: {
      hover: 'hover:bg-gray-800',
      active: 'active:bg-gray-700',
      disabled: 'disabled:bg-gray-600',
      disabledText: 'disabled:text-gray-400',
      focus: 'focus:ring-green-400',
    },
    
    chart: {
      primary: '#10b981',
      secondary: '#3b82f6',
      accent: '#f59e0b',
      danger: '#ef4444',
      purple: '#8b5cf6',
      pink: '#ec4899',
      teal: '#14b8a6',
      orange: '#f97316',
      grid: '#374151',
      axis: '#9ca3af',
      dotStroke: '#1f2937',
      tooltip: {
        background: '#374151',
        border: '#6b7280',
        text: '#f9fafb',
        textSecondary: '#d1d5db',
      },
    },
    
    ui: {
      blue: {
        background: 'bg-blue-600',
        backgroundLight: 'bg-blue-50',
        text: 'text-blue-600',
        textDark: 'text-blue-800',
        border: 'border-blue-600',
        borderLight: 'border-blue-200',
      },
      green: {
        background: 'bg-green-600',
        backgroundLight: 'bg-green-100',
        text: 'text-green-600',
        textDark: 'text-green-800',
      },
      red: {
        background: 'bg-red-600',
        backgroundLight: 'bg-red-50',
        text: 'text-red-600',
        textDark: 'text-red-800',
        border: 'border-red-500',
      },
      yellow: {
        background: 'bg-yellow-600',
        backgroundLight: 'bg-yellow-50',
        text: 'text-yellow-600',
        textDark: 'text-yellow-800',
        border: 'border-yellow-200',
      },
      orange: {
        background: 'bg-orange-600',
        backgroundLight: 'bg-orange-100',
        text: 'text-orange-600',
        textDark: 'text-orange-800',
      },
      purple: {
        background: 'bg-purple-600',
        backgroundLight: 'bg-purple-50',
        text: 'text-purple-600',
        border: 'border-purple-200',
        borderLight: 'border-purple-300',
      },
      indigo: {
        background: 'bg-indigo-600',
        backgroundLight: 'bg-indigo-50',
      },
    },
    
    propertyAnalysis: {
      pageBackground: 'bg-gradient-to-br from-gray-900 via-gray-950 to-black',
      headerBackground: 'bg-gray-900/90',
      cardBackground: 'bg-gray-700/20',
      cardBackgroundNested: 'bg-gray-800/20',
      animatedEffect1: 'bg-white/5',
      animatedEffect2: 'bg-white/8',
      animatedEffect3: 'bg-white/3',
      shadow: 'shadow-black/50',
    },
    
    recommendation: {
      buy: {
        background: 'bg-green-600/20',
        text: 'text-green-300',
        border: 'border-green-600',
        textHeading: 'text-green-400',
        textBullet: 'text-green-500',
      },
      hold: {
        background: 'bg-yellow-600/20',
        text: 'text-yellow-300',
        border: 'border-yellow-600',
        textHeading: 'text-yellow-400',
        textBullet: 'text-yellow-500',
      },
      avoid: {
        background: 'bg-red-600/20',
        text: 'text-red-300',
        border: 'border-red-600',
        textHeading: 'text-red-400',
        textBullet: 'text-red-500',
      },
      negotiate: {
        background: 'bg-orange-600/20',
        text: 'text-orange-300',
        border: 'border-orange-600',
        textHeading: 'text-orange-400',
        textBullet: 'text-orange-500',
      },
      neutral: {
        background: 'bg-blue-600/20',
        text: 'text-blue-300',
        border: 'border-blue-600',
        textHeading: 'text-blue-400',
        textBullet: 'text-blue-500',
      },
    },
    
    actionItems: {
      immediate: {
        background: 'bg-red-900/20',
        border: 'border-red-700',
        textHeading: 'text-red-400',
        textBullet: 'text-red-500',
      },
      shortTerm: {
        background: 'bg-yellow-900/20',
        border: 'border-yellow-700',
        textHeading: 'text-yellow-400',
        textBullet: 'text-yellow-500',
      },
      longTerm: {
        background: 'bg-blue-900/20',
        border: 'border-blue-700',
        textHeading: 'text-blue-400',
        textBullet: 'text-blue-500',
      },
    },
    
    tabs: {
      active: {
        text: 'text-blue-400',
        border: 'border-blue-400',
      },
      inactive: {
        text: 'text-gray-400',
        hover: 'hover:text-white',
      },
    },
    
    validation: {
      errorText: 'text-red-500',
      errorIndicator: 'bg-red-500',
      errorTitle: 'text-red-500',
    },
    
    values: {
      fairMarketValue: 'text-green-400',
      recommendedOffer: 'text-blue-400',
      positive: 'text-green-400',
      negative: 'text-red-400',
    },
    
    liveData: {
      background: 'bg-green-900/50',
      text: 'text-green-200',
      border: 'border-green-700',
    },
  },
  
  borders: {
    thin: 'border',
    medium: 'border-2',
    thick: 'border-4',
    dashed: 'border-dashed',
    bubble: 'border-2',
  },
  
  spacing: {
    xs: 'p-1',
    sm: 'p-2',
    md: 'p-4',
    lg: 'p-6',
    xl: 'p-8',
    xxl: 'p-12',
  },
  
  radius: {
    sm: 'rounded',
    md: 'rounded-lg',
    lg: 'rounded-xl',
    full: 'rounded-full',
  },
  
  opacity: {
    light: '/20',
    medium: '/30',
    heavy: '/50',
  },
};

// Light theme - Enhanced with vibrant colors and proper contrast
export const lightTheme: Theme = {
  colors: {
    background: {
      primary: 'bg-white',
      secondary: 'bg-slate-50',
      tertiary: 'bg-blue-50/30',
      card: 'bg-white',
      overlay: 'bg-white/80',
      loading: 'bg-blue-100/40',
      error: 'bg-red-50',
      errorLight: 'bg-red-100/50',
      warning: 'bg-amber-50',
      warningLight: 'bg-amber-100/60',
      white: 'bg-white',
      gray50: 'bg-gray-50',
      gray100: 'bg-gray-100',
      gray200: 'bg-gray-200',
      gray400: 'bg-gray-400',
    },
    
    text: {
      primary: 'text-slate-900',
      secondary: 'text-slate-700',
      tertiary: 'text-slate-600',
      muted: 'text-slate-500',
      placeholder: 'placeholder-slate-400',
      inverse: 'text-white',
      loading: 'text-slate-500',
      info: 'text-blue-700',
      warning: 'text-amber-800',
      warningLight: 'text-amber-700',
      warningText: 'text-amber-900',
      error: 'text-red-700',
      errorLight: 'text-red-800',
      errorHover: 'text-red-900',
      gray400: 'text-gray-400',
      gray500: 'text-gray-500',
      gray600: 'text-gray-600',
      gray700: 'text-gray-700',
      gray800: 'text-gray-800',
      gray900: 'text-gray-900',
    },
    
    brand: {
      primary: 'bg-blue-600',
      primaryHover: 'hover:bg-blue-700',
      primaryLight: 'text-blue-600',
      accent: 'text-blue-700',
    },
    
    status: {
      success: 'bg-emerald-600',
      successHover: 'hover:bg-emerald-700',
      successLight: 'text-emerald-700',
      successText: 'text-white',
      warning: 'bg-amber-500',
      warningHover: 'hover:bg-amber-600',
      warningLight: 'text-amber-700',
      warningText: 'text-amber-900',
      warningBackground: 'bg-amber-50',
      error: 'bg-red-600',
      errorHover: 'hover:bg-red-700',
      errorLight: 'text-red-700',
      errorText: 'text-white',
      errorBackground: 'bg-red-50',
      info: 'bg-blue-600',
      infoHover: 'hover:bg-blue-700',
      infoLight: 'text-blue-700',
      infoText: 'text-white',
    },
    
    border: {
      primary: 'border-slate-200',
      secondary: 'border-slate-300',
      tertiary: 'border-slate-400',
      dashed: 'border-slate-300',
      dashedHover: 'hover:border-blue-400',
      divider: 'border-slate-200',
      error: 'border-red-400',
      warning: 'border-amber-400',
      loading: 'border-blue-300',
    },
    
    interactive: {
      hover: 'hover:bg-slate-100',
      active: 'active:bg-slate-200',
      disabled: 'disabled:bg-slate-200',
      disabledText: 'disabled:text-slate-400',
      focus: 'focus:ring-blue-500 focus:ring-2',
    },
    
    chart: {
      primary: '#059669',
      secondary: '#2563eb',
      accent: '#d97706',
      danger: '#dc2626',
      purple: '#7c3aed',
      pink: '#db2777',
      teal: '#0d9488',
      orange: '#ea580c',
      grid: '#cbd5e1',
      axis: '#64748b',
      dotStroke: '#ffffff',
      tooltip: {
        background: '#ffffff',
        border: '#e2e8f0',
        text: '#0f172a',
        textSecondary: '#475569',
      },
    },
    
    ui: {
      blue: {
        background: 'bg-blue-600',
        backgroundLight: 'bg-blue-50',
        text: 'text-blue-600',
        textDark: 'text-blue-800',
        border: 'border-blue-600',
        borderLight: 'border-blue-200',
      },
      green: {
        background: 'bg-green-600',
        backgroundLight: 'bg-green-100',
        text: 'text-green-600',
        textDark: 'text-green-800',
      },
      red: {
        background: 'bg-red-600',
        backgroundLight: 'bg-red-50',
        text: 'text-red-600',
        textDark: 'text-red-800',
        border: 'border-red-500',
      },
      yellow: {
        background: 'bg-yellow-600',
        backgroundLight: 'bg-yellow-50',
        text: 'text-yellow-600',
        textDark: 'text-yellow-800',
        border: 'border-yellow-200',
      },
      orange: {
        background: 'bg-orange-600',
        backgroundLight: 'bg-orange-100',
        text: 'text-orange-600',
        textDark: 'text-orange-800',
      },
      purple: {
        background: 'bg-purple-600',
        backgroundLight: 'bg-purple-50',
        text: 'text-purple-600',
        border: 'border-purple-200',
        borderLight: 'border-purple-300',
      },
      indigo: {
        background: 'bg-indigo-600',
        backgroundLight: 'bg-indigo-50',
      },
    },
    
    propertyAnalysis: {
      pageBackground: 'bg-gradient-to-br from-blue-50 via-indigo-50/50 to-white',
      headerBackground: 'bg-white/95 backdrop-blur-md',
      cardBackground: 'bg-white/90 backdrop-blur-sm',
      cardBackgroundNested: 'bg-slate-50/80',
      animatedEffect1: 'bg-blue-400/10',
      animatedEffect2: 'bg-indigo-400/15',
      animatedEffect3: 'bg-purple-400/8',
      shadow: 'shadow-slate-900/10',
    },
    
    recommendation: {
      buy: {
        background: 'bg-emerald-50',
        text: 'text-emerald-800',
        border: 'border-emerald-500',
        textHeading: 'text-emerald-700',
        textBullet: 'text-emerald-600',
      },
      hold: {
        background: 'bg-amber-50',
        text: 'text-amber-800',
        border: 'border-amber-500',
        textHeading: 'text-amber-700',
        textBullet: 'text-amber-600',
      },
      avoid: {
        background: 'bg-red-50',
        text: 'text-red-800',
        border: 'border-red-500',
        textHeading: 'text-red-700',
        textBullet: 'text-red-600',
      },
      negotiate: {
        background: 'bg-orange-50',
        text: 'text-orange-800',
        border: 'border-orange-500',
        textHeading: 'text-orange-700',
        textBullet: 'text-orange-600',
      },
      neutral: {
        background: 'bg-blue-50',
        text: 'text-blue-800',
        border: 'border-blue-500',
        textHeading: 'text-blue-700',
        textBullet: 'text-blue-600',
      },
    },
    
    actionItems: {
      immediate: {
        background: 'bg-red-50/80',
        border: 'border-red-300',
        textHeading: 'text-red-700',
        textBullet: 'text-red-600',
      },
      shortTerm: {
        background: 'bg-amber-50/80',
        border: 'border-amber-300',
        textHeading: 'text-amber-700',
        textBullet: 'text-amber-600',
      },
      longTerm: {
        background: 'bg-blue-50/80',
        border: 'border-blue-300',
        textHeading: 'text-blue-700',
        textBullet: 'text-blue-600',
      },
    },
    
    tabs: {
      active: {
        text: 'text-blue-700',
        border: 'border-blue-600',
      },
      inactive: {
        text: 'text-slate-600',
        hover: 'hover:text-slate-900',
      },
    },
    
    validation: {
      errorText: 'text-red-700',
      errorIndicator: 'bg-red-600',
      errorTitle: 'text-red-700',
    },
    
    values: {
      fairMarketValue: 'text-emerald-700',
      recommendedOffer: 'text-blue-700',
      positive: 'text-emerald-700',
      negative: 'text-red-700',
    },
    
    liveData: {
      background: 'bg-emerald-100',
      text: 'text-emerald-900',
      border: 'border-emerald-500',
    },
  },
  
  borders: {
    thin: 'border',
    medium: 'border',
    thick: 'border-2',
    dashed: 'border-dashed',
    bubble: 'border-[1.25px]',
  },
  
  spacing: {
    xs: 'p-1',
    sm: 'p-2',
    md: 'p-4',
    lg: 'p-6',
    xl: 'p-8',
    xxl: 'p-12',
  },
  
  radius: {
    sm: 'rounded',
    md: 'rounded-lg',
    lg: 'rounded-xl',
    full: 'rounded-full',
  },
  
  opacity: {
    light: '/20',
    medium: '/30',
    heavy: '/50',
  },
};

// Theme context and provider
export const defaultTheme = darkTheme;

// Utility function to combine theme classes
export const combineClasses = (...classes: (string | undefined | null)[]): string => {
  return classes.filter(Boolean).join(' ');
};

// Chart color arrays - dynamic function based on theme
export const getChartColors = (theme: Theme) => [
  theme.colors.chart.primary,
  theme.colors.chart.secondary,
  theme.colors.chart.accent,
  theme.colors.chart.purple,
  theme.colors.chart.pink,
  theme.colors.chart.teal,
  theme.colors.chart.orange,
  theme.colors.chart.danger
];

// Legacy export for backward compatibility (uses dark theme)
export const chartColors = getChartColors(darkTheme);

// Predefined component style combinations
export const componentStyles = {
  // Card styles
  card: {
    container: `${darkTheme.colors.background.card} ${darkTheme.colors.border.primary} ${darkTheme.borders.thin} ${darkTheme.radius.md}`,
    header: `${darkTheme.colors.text.primary}`,
    description: `${darkTheme.colors.text.tertiary}`,
  },
  
  // Button styles
  button: {
    primary: `${darkTheme.colors.brand.primary} ${darkTheme.colors.brand.primaryHover} ${darkTheme.colors.text.inverse}`,
    secondary: `${darkTheme.colors.border.secondary} ${darkTheme.colors.text.tertiary} ${darkTheme.colors.interactive.hover} bg-transparent`,
    ghost: `${darkTheme.colors.text.tertiary} ${darkTheme.colors.interactive.hover} ${darkTheme.colors.text.primary}`,
  },
  
  // Input styles
  input: {
    textarea: `${darkTheme.colors.background.tertiary} ${darkTheme.colors.border.secondary} ${darkTheme.colors.text.primary} ${darkTheme.colors.text.placeholder}`,
  },
  
  // Status styles
  status: {
    success: `${darkTheme.colors.status.successLight}`,
    warning: `${darkTheme.colors.status.warningLight}`,
    error: `${darkTheme.colors.status.errorLight}`,
    info: `${darkTheme.colors.status.infoLight}`,
  },
  
  // File upload styles
  upload: {
    area: `${darkTheme.borders.medium} ${darkTheme.borders.dashed} ${darkTheme.colors.border.dashed} ${darkTheme.colors.border.dashedHover} ${darkTheme.radius.md}`,
    rejected: `${darkTheme.colors.status.errorBackground} ${darkTheme.colors.border.error} ${darkTheme.borders.thin} ${darkTheme.radius.md}`,
    supported: `${darkTheme.colors.background.tertiary}`,
    unsupported: `${darkTheme.colors.status.warningBackground} ${darkTheme.colors.border.warning} ${darkTheme.borders.thin}`,
  },
  
  // Chat styles
  chat: {
    userMessage: `${darkTheme.colors.brand.primary} ${darkTheme.colors.text.inverse}`,
    assistantMessage: `${darkTheme.colors.background.tertiary} ${darkTheme.colors.text.primary} ${darkTheme.colors.border.secondary} ${darkTheme.borders.thin}`,
    loading: `${darkTheme.colors.background.tertiary} ${darkTheme.colors.border.secondary} ${darkTheme.borders.thin} ${darkTheme.radius.md}`,
  },
};

// Light theme specific component styles
export const lightThemeComponentStyles = {
  // File upload styles with thin light borders
  upload: {
    area: `${lightTheme.borders.medium} ${lightTheme.borders.dashed} ${lightTheme.colors.border.dashed} ${lightTheme.colors.border.dashedHover} ${lightTheme.radius.md}`,
    rejected: `${lightTheme.colors.status.errorBackground} ${lightTheme.colors.border.error} ${lightTheme.borders.thin} ${lightTheme.radius.md}`,
    supported: `${lightTheme.colors.background.tertiary} border border-gray-300`,
    unsupported: `${lightTheme.colors.status.warningBackground} ${lightTheme.colors.border.warning} border border-gray-300`,
  },
  
  // High contrast chat styles for light theme
  chat: {
    userMessage: `${lightTheme.colors.brand.primary} ${lightTheme.colors.text.inverse}`,
    assistantMessage: `${lightTheme.colors.background.tertiary} ${lightTheme.colors.text.primary} ${lightTheme.colors.border.secondary} ${lightTheme.borders.thin}`,
    loading: `${lightTheme.colors.background.tertiary} ${lightTheme.colors.border.secondary} ${lightTheme.borders.thin} ${lightTheme.radius.md}`,
  },
  
  // High contrast card styles for light theme
  card: {
    container: `${lightTheme.colors.background.card} ${lightTheme.colors.border.primary} ${lightTheme.borders.thin} ${lightTheme.radius.md}`,
    header: `${lightTheme.colors.text.primary}`,
    description: `${lightTheme.colors.text.tertiary}`,
  },
  
  // High contrast button styles for light theme
  button: {
    primary: `${lightTheme.colors.brand.primary} ${lightTheme.colors.brand.primaryHover} ${lightTheme.colors.text.inverse}`,
    secondary: `${lightTheme.colors.border.secondary} ${lightTheme.colors.text.tertiary} ${lightTheme.colors.interactive.hover} bg-transparent`,
    ghost: `${lightTheme.colors.text.tertiary} ${lightTheme.colors.interactive.hover} ${lightTheme.colors.text.primary}`,
  },
};
